import { useEffect, useState } from "react";
import {
  fetchGlobalTenantConfig,
  type GlobalTenantConfig,
} from "../services/globalConfigService";

export interface UseGlobalConfigResult {
  config: GlobalTenantConfig | null;
  loading: boolean;
  error: string | null;
}

let cachedConfig: GlobalTenantConfig | null = null;
let inFlight: Promise<GlobalTenantConfig | null> | null = null;

async function loadConfigOnce(): Promise<GlobalTenantConfig | null> {
  if (cachedConfig) return cachedConfig;
  if (inFlight !== null) return inFlight;

  inFlight = fetchGlobalTenantConfig()
    .then((res) => {
      cachedConfig = res?.data ?? null;
      return cachedConfig;
    })
    .catch((err: unknown) => {
      cachedConfig = null;
      const msg = err instanceof Error ? err.message : "Failed to load config";
      throw new Error(msg);
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

export function useGlobalConfig(): UseGlobalConfigResult {
  const [config, setConfig] = useState<GlobalTenantConfig | null>(cachedConfig);
  const [loading, setLoading] = useState<boolean>(!cachedConfig);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    if (cachedConfig) {
      setLoading(false);
      return () => {
        mounted = false;
      };
    }

    setLoading(true);
    setError(null);
    loadConfigOnce()
      .then((c) => {
        if (!mounted) return;
        setConfig(c);
      })
      .catch((e: unknown) => {
        if (!mounted) return;
        setError(e instanceof Error ? e.message : "Failed to load config");
        setConfig(null);
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  return { config, loading, error };
}

export function __resetGlobalConfigCacheForTests(): void {
  cachedConfig = null;
  inFlight = null;
}

